import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { loadPublicDomains, guestBase, taxBase } from "../publicDomains";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, Copy, ExternalLink, RefreshCw, Ban, Eye, Loader2, Send } from "lucide-react";

const OPEN_LABELS = {
  never_opened: { label: "Mai aperto", cls: "bg-slate-100 text-slate-600" },
  opened: { label: "Aperto", cls: "bg-sky-100 text-sky-700" },
  checkin_started: { label: "Check-in iniziato", cls: "bg-amber-100 text-amber-800" },
  completed: { label: "Completato", cls: "bg-emerald-100 text-emerald-700" },
};

export default function GuestPortalLink({ bookingId }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [domains, setDomains] = useState(null);
  useEffect(() => { loadPublicDomains().then(setDomains); }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/bookings/${bookingId}/portal-link`).then((r) => setSt(r.data)).catch(() => setSt(null)).finally(() => setLoading(false));
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const fullUrl = st?.portal_code ? `${guestBase(domains)}/p/${st.portal_code}` : "";
  const taxUrl = st?.portal_code ? `${taxBase(domains)}/tax/${st.portal_code}` : "";

  const act = async (action) => {
    setBusy(action);
    try {
      const url = action === "create" ? `/bookings/${bookingId}/portal-link` :
        action === "revoke" ? `/bookings/${bookingId}/portal-link/revoke` :
          `/bookings/${bookingId}/portal-link/regenerate`;
      const r = await api.post(url);
      setSt(r.data);
      toast.success(action === "revoke" ? "Link revocato" : action === "regenerate" ? "Nuovo link generato" : "Link Portale ospite generato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const copy = () => { navigator.clipboard.writeText(fullUrl); toast.success("Link copiato"); };
  const copyTax = () => { navigator.clipboard.writeText(taxUrl); toast.success("Link Tax copiato"); };
  const sendTax = async () => {
    const first = (st?.guest_name || "").split(" ")[0];
    const msg = `Ciao ${first}, per completare la tua prenotazione a ${st?.apartment_name || ""}${st?.check_in ? ` (arrivo ${st.check_in})` : ""} puoi pagare la tassa di soggiorno in modo sicuro qui: ${taxUrl} — bastano pochi istanti. Grazie!`;
    try { await navigator.clipboard.writeText(msg); toast.success("Messaggio + link Tax copiati — incollalo su WhatsApp/email"); }
    catch { toast.error("Impossibile copiare"); }
  };

  if (loading) return <div className="py-4 text-center text-sm text-slate-400"><Loader2 size={16} className="mr-2 inline animate-spin" />Caricamento…</div>;

  const os = OPEN_LABELS[st?.open_status] || OPEN_LABELS.never_opened;

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4" data-testid="portal-link-panel">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase text-indigo-700"><Link2 size={14} /> Portale ospite</h4>
        {st?.portal_code && !st?.revoked && <Badge className={os.cls}>{os.label}</Badge>}
        {st?.revoked && <Badge className="bg-rose-100 text-rose-700">Revocato</Badge>}
      </div>

      {!st?.portal_code || st?.revoked ? (
        <Button data-testid="portal-generate-btn" size="sm" disabled={busy === "create"} onClick={() => act("create")} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
          {busy === "create" ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Genera link Portale ospite
        </Button>
      ) : (
        <>
          <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2">
            <code data-testid="portal-link-url" className="flex-1 truncate text-xs text-slate-700">{fullUrl}</code>
            <button data-testid="portal-copy-btn" onClick={copy} title="Copia" className="text-slate-500 hover:text-indigo-600"><Copy size={15} /></button>
            <a data-testid="portal-preview-btn" href={fullUrl} target="_blank" rel="noreferrer" title="Anteprima" className="text-slate-500 hover:text-indigo-600"><Eye size={15} /></a>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {st.open_count > 0 && <span>{st.open_count} aperture</span>}
            {st.terms_accepted && <Badge className="bg-emerald-100 text-emerald-700">Condizioni accettate</Badge>}
            <div className="ml-auto flex gap-1.5">
              <Button data-testid="portal-regenerate-btn" size="sm" variant="outline" disabled={busy === "regenerate"} onClick={() => act("regenerate")} className="h-7 gap-1 px-2 text-xs">
                {busy === "regenerate" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Rigenera
              </Button>
              <Button data-testid="portal-revoke-btn" size="sm" variant="outline" disabled={busy === "revoke"} onClick={() => act("revoke")} className="h-7 gap-1 px-2 text-xs text-rose-600 hover:text-rose-700">
                {busy === "revoke" ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Revoca
              </Button>
            </div>
          </div>
          {st.channel === "airbnb" ? (
            <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-700" data-testid="portal-tax-airbnb-note">
              Tassa di soggiorno riscossa da Airbnb — link Tax non necessario.
            </div>
          ) : (
            <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase text-emerald-700">Tax</span>
                <code data-testid="tax-link-url" className="flex-1 truncate text-xs text-slate-700">{taxUrl}</code>
                <button data-testid="tax-copy-btn" onClick={copyTax} title="Copia link Tax" className="text-slate-500 hover:text-emerald-700"><Copy size={15} /></button>
                <a data-testid="tax-preview-btn" href={taxUrl} target="_blank" rel="noreferrer" title="Anteprima" className="text-slate-500 hover:text-emerald-700"><Eye size={15} /></a>
              </div>
              <Button data-testid="tax-send-btn" size="sm" variant="outline" onClick={sendTax} className="mt-2 h-7 gap-1 border-emerald-300 px-2 text-xs text-emerald-700 hover:bg-emerald-100">
                <Send size={13} /> Invia link Tax
              </Button>
              <p className="mt-1 text-[10px] text-slate-400">Uso manuale (ospiti che non completano il portale, recupero pagamenti). La tassa resta parte del normale check-in nel Guest Portal.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
